import React, { useMemo } from 'react';

type SummaryRow = {
    class: number | null;
    description: string | null;
    count: number | null;
    unit: string | null;
    total: string | null;
};

type Segment = {
    label: string;
    count: number;
    percentage: number;
    colorClass: string;
};

const COLOR_CLASSES: string[] = [
    'bg-emerald-500',
    'bg-sky-500',
    'bg-amber-500',
    'bg-fuchsia-500',
    'bg-cyan-500',
    'bg-rose-500',
    'bg-indigo-500',
    'bg-lime-500',
    'bg-violet-500',
    'bg-teal-500',
];

type Props = {
	summary?: SummaryRow[];
};

const SummaryChart: React.FC<Props> = ({ summary = [] }) => {

	const { segments, totalCount } = useMemo(() => {
		const valid = summary.filter((r) => r.class !== null && typeof r.count === 'number' && r.count !== null) as Array<
            Required<Pick<SummaryRow, 'description' | 'count'>>
        >;
        const total = valid.reduce((acc, r) => acc + (r.count ?? 0), 0);
        const segs: Segment[] = valid.map((r, idx) => {
            const percentage = total > 0 ? (100 * (r.count ?? 0)) / total : 0;
            return {
                label: r.description || '',
                count: r.count ?? 0,
                percentage,
                colorClass: COLOR_CLASSES[idx % COLOR_CLASSES.length],
            };
        });
		return { segments: segs, totalCount: total };
	}, [summary]);

    return (
        <div className="flex-1" role="region" aria-label="Verteilung der Team-Typen">
            <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-800">Verteilung nach Team-Typen</p>
                <p className="text-xs text-gray-500">Gesamt: {totalCount} Teams</p>
            </div>

            {/* Stacked bar (100%) */}
            <div className="w-full h-3 rounded-full overflow-hidden">
                <div className="flex h-full gap-0.5">
                    {segments.map((s, i) => (
                        <div
                            key={i}
                            className={`${s.colorClass} h-full`}
                            style={{ width: `${s.percentage}%` }}
                            title={`${s.label}: ${s.count} Teams (${s.percentage.toFixed(0)}%)`}
                            aria-label={`${s.label}: ${s.count} Teams (${s.percentage.toFixed(0)}%)`}
                        />
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                {segments.map((s, i) => (
                    <div key={`legend-${i}`} className="inline-flex items-center gap-1.5">
                        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${s.colorClass}`} />
                        <span className="whitespace-nowrap">
                            {s.label} — {s.percentage.toFixed(0)}% ({s.count})
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SummaryChart;


